import React from 'react';
import { Link } from 'react-router';

import LegalLayout, { LegalSection } from './LegalLayout';

const SUPPORT_EMAIL = 'support@snappytrade.app';

/**
 * Terms of Use for Snappy Trade. Original text written for this product;
 * review by qualified counsel before public launch is strongly recommended.
 */
const Terms: React.FC = () => (
    <LegalLayout
        title="Terms of Use"
        effectiveDate="October 4, 2026"
        counterpart={{ label: 'Privacy Policy →', to: '/privacy' }}
    >
        <LegalSection title="1. Agreement to these Terms">
            <p>
                These Terms of Use (the &quot;Terms&quot;) govern your access to
                and use of the Snappy Trade website, web application and related
                services (together, the &quot;Service&quot;), operated by the
                Snappy Trade team (&quot;Snappy Trade&quot;, &quot;we&quot;,
                &quot;us&quot; or &quot;our&quot;). By signing in, creating,
                importing or restoring a Snappy Wallet in your browser, or
                otherwise using the Service, you agree to be bound by these
                Terms and by our{' '}
                <Link to="/privacy" className="text-info hover:underline">
                    Privacy Policy
                </Link>
                . If you do not agree, do not use the Service.
            </p>
        </LegalSection>

        <LegalSection title="2. The Service">
            <p>
                Snappy Trade is self-custody wallet software with a trading
                interface for crypto-assets. Your wallet key is created or
                restored in your browser and never leaves it. Every order you
                place is signed in your browser and sent directly to third-party
                decentralized trading infrastructure built on Hyperliquid
                technology, which we do not operate; we never hold your funds
                and we never sign or execute anything for you. The Service also
                offers a <strong>demo mode</strong> that uses live market prices
                but simulates trade execution against a virtual balance; demo
                results do not involve real funds and are not indicative of real
                trading outcomes.
            </p>
            <p>
                The Service is under active development and is provided on a
                beta basis. Features may change, be interrupted, or be
                discontinued at any time without notice.
            </p>
        </LegalSection>

        <LegalSection title="3. Eligibility and Restricted Jurisdictions">
            <p>By using the Service you represent and warrant that you:</p>
            <ul>
                <li>
                    are at least 18 years old and of the age of majority where
                    you live, with full legal capacity to enter into these
                    Terms;
                </li>
                <li>
                    are <strong>not</strong> a citizen or resident of, located
                    in, or accessing the Service from the United States of
                    America or its territories (a &quot;US Person&quot;);
                </li>
                <li>
                    are not located in, organized in, or a resident of Cuba,
                    Iran, North Korea, Syria, the Crimea, Donetsk or Luhansk
                    regions of Ukraine, or any other jurisdiction subject to
                    comprehensive sanctions;
                </li>
                <li>
                    are not named on any sanctions list, including the US
                    Treasury OFAC Specially Designated Nationals list, and are
                    not acting on behalf of anyone who is;
                </li>
                <li>
                    are permitted to use services like ours under the laws of
                    your jurisdiction, and you accept sole responsibility for
                    confirming this before use.
                </li>
            </ul>
            <p>
                We may use technical measures to restrict access from prohibited
                jurisdictions, and you agree not to circumvent them (including
                through VPNs or similar tools). We may suspend or terminate
                access where we believe these requirements are not met.
            </p>
        </LegalSection>

        <LegalSection title="4. Accounts, Wallets and Security">
            <p>
                Using the Service with real funds includes signing in with a
                social account you control (Google, Discord, X or Telegram), so
                that we can recognise you and hold your encrypted wallet backup.
                Signing in never gives us access to your wallet or your funds.
                You are solely responsible for the security of the browser and
                device you use, your wallet&apos;s recovery phrase, your Snappy
                backup password and recovery key, and the social accounts you
                sign in with. Anyone with access to them may be able to act on
                your behalf, and we cannot recover keys or reverse actions taken
                with them. We may suspend or terminate access to the Service at
                our discretion, including for suspected fraud, abuse, or breach
                of these Terms; because the Service is self-custody software,
                this never gives us control of your funds.
            </p>
        </LegalSection>

        <LegalSection title="5. The Snappy Wallet (Self-Custody)">
            <p>
                The Snappy Wallet is self-custody software. Its key is generated
                or restored in your browser and kept only in that browser&apos;s
                local storage until you sign out or forget the wallet. We never
                receive, store or have access to your key, your recovery phrase,
                your backup password or your recovery key. We therefore cannot
                access, move, freeze or recover your funds, reverse your
                transactions, or reset your backup password or recovery key. You
                acknowledge and agree that:
            </p>
            <ul>
                <li>
                    every trade, deposit and withdrawal is signed in your
                    browser in response to your own action and sent directly to
                    Hyperliquid, the relevant blockchain or a bridging provider;
                    we do not hold, sign or execute them for you;
                </li>
                <li>
                    the Snappy backup is your recovery phrase encrypted in your
                    browser with your password and a recovery key that only you
                    hold; Snappy stores only that encrypted copy and cannot
                    read, recover or reset it;
                </li>
                <li>
                    if you lose access to the browser holding your wallet and do
                    not have your recovery phrase, or your Snappy backup with
                    its password and recovery key, your wallet and its funds are
                    permanently lost, and no one, including us, can restore
                    them;
                </li>
                <li>
                    your funds are held in your own wallet and your own
                    Hyperliquid account, not by us; the Snappy Wallet is not a
                    bank account, and balances are not deposits, earn no
                    interest, and are not covered by any deposit insurance or
                    investor compensation scheme;
                </li>
                <li>
                    a browser is a shared environment: anyone who can use the
                    browser or read its stored data could use your wallet, so
                    sign out or forget the wallet on devices you do not control;
                </li>
                <li>
                    self-custody involves risk, including loss of funds through
                    software defects, a compromised browser or device, malware,
                    or failure of the underlying infrastructure, and you accept
                    that risk by using the Service.
                </li>
            </ul>
        </LegalSection>

        <LegalSection title="6. Deposits and Withdrawals">
            <p>
                You deposit by sending supported tokens on a supported network
                to your own wallet address. The Service helps you move them to
                your Hyperliquid account through third-party bridging and
                relaying infrastructure, with transactions you sign in your
                browser; a transaction on the origin network may need a small
                amount of that network&apos;s native token for gas. Withdrawals
                are sent to the address and network you enter and confirm.
                Blockchain transactions are irreversible: funds sent to a wrong
                address, on a wrong network, or in an unsupported token may be
                permanently lost, and we have no obligation or ability to
                recover them. Processing times and fees depend on third-party
                networks and providers and are not guaranteed.
            </p>
        </LegalSection>

        <LegalSection title="7. Trading and Fees">
            <p>
                Trading crypto-assets is high-risk. Prices are volatile and can
                move quickly, and you can lose your entire deposited balance.
                Trading rules, order handling and settlement are set by the
                underlying trading venue and by market conditions, and can
                change without notice. Prices, quotes and estimated profit or
                loss shown in the interface are indicative only and may differ
                from actual execution.
            </p>
            <p>
                Use of the Service may incur fees, spreads and network costs,
                which may change from time to time. These include the trading
                venue&apos;s own fees and a builder fee that we receive on
                orders placed through the Service (currently at most 0.08% of
                the order value), which you approve on Hyperliquid, with a
                signature from your own wallet, before your first real trade.
            </p>
        </LegalSection>

        <LegalSection title="8. No Investment Advice; No Fiduciary Duty">
            <p>
                Nothing in the Service, including market data, charts,
                statistics or any other content, is investment, financial,
                legal, accounting or tax advice, or a recommendation to buy,
                sell or hold any asset. We act solely as a software interface
                provider; no advisory, brokerage, agency, trust or fiduciary
                relationship is created between you and us. Always do your own
                research and consider independent professional advice.
            </p>
        </LegalSection>

        <LegalSection title="9. Regulatory Status">
            <p>
                Snappy Trade is not registered or licensed with any securities,
                commodities or other financial regulator in any jurisdiction,
                and the Service has not been reviewed or approved by any such
                authority.
            </p>
            <p>
                We are a software provider. We do not provide custody or
                administration of crypto-assets, execution or reception and
                transmission of orders, transfer services, exchange services,
                portfolio management or advice on behalf of anyone: your key
                stays in your browser, you sign every order and transfer
                yourself, and your browser sends them directly to the venue, the
                blockchain or the bridging provider. Under Regulation (EU)
                2023/1114 (MiCA), providing custody and administration of
                crypto-assets means safekeeping or controlling, on behalf of
                clients, crypto-assets or the means of access to them, such as
                private keys (Article 3(1)(17)); we do neither, and MiCA states
                that hardware or software providers of non-custodial wallets do
                not fall within its scope (Recital 83). We have therefore not
                sought and do not hold an authorisation as a crypto-asset
                service provider.
            </p>
        </LegalSection>

        <LegalSection title="10. Assumption of Risk">
            <p>
                You use the Service at your own risk. Without limiting the rest
                of these Terms, you acknowledge risks including:
            </p>
            <ul>
                <li>extreme price volatility of digital assets;</li>
                <li>
                    partial or total loss of funds through software defects,
                    smart-contract vulnerabilities, bridge failures, oracle
                    failures or network congestion;
                </li>
                <li>
                    dependence on third-party systems (trading venues,
                    blockchains, bridges, RPC providers, sign-in providers) that
                    we do not control;
                </li>
                <li>
                    evolving and uncertain legal and regulatory treatment of
                    digital assets in many jurisdictions;
                </li>
                <li>
                    the possibility of interface errors, delayed data, or
                    downtime during which orders cannot be placed or cancelled.
                </li>
            </ul>
            <p>
                Never deposit more than you can afford to lose. To the fullest
                extent permitted by law, you release us from any claims arising
                out of these risks.
            </p>
        </LegalSection>

        <LegalSection title="11. Acceptable Use">
            <p>You agree not to:</p>
            <ul>
                <li>
                    use the Service in violation of any law, including
                    sanctions, anti-money-laundering and market-abuse rules;
                </li>
                <li>
                    engage in market manipulation, exploit software defects, or
                    attack, probe, overload or interfere with the Service or its
                    infrastructure;
                </li>
                <li>
                    misrepresent your identity or location, or circumvent
                    geographic or other access restrictions;
                </li>
                <li>
                    use bots, scrapers or automated means to access the Service
                    other than through interfaces we make available;
                </li>
                <li>
                    use the Service on behalf of a third party or permit a
                    restricted person to use it through your access.
                </li>
            </ul>
            <p>
                We may investigate suspected violations and take any action we
                consider appropriate, including suspending access and reporting
                to authorities.
            </p>
        </LegalSection>

        <LegalSection title="12. Intellectual Property and Feedback">
            <p>
                The Service, including its software, design, branding, text and
                graphics, is owned by us or our licensors and is protected by
                intellectual-property laws. We grant you a limited, revocable,
                non-exclusive, non-transferable licence to use the Service for
                its intended purpose. If you send us feedback or suggestions,
                you grant us a perpetual, irrevocable, royalty-free licence to
                use them without restriction or compensation.
            </p>
        </LegalSection>

        <LegalSection title="13. Third-Party Services">
            <p>
                The Service depends on and links to services we do not control,
                including the Hyperliquid trading infrastructure, the
                blockchains you deposit from and withdraw to, bridging and
                relaying providers, RPC providers, and the Google, Discord, X
                and Telegram sign-in services. Your browser talks to several of
                them directly. Your use of those services is governed by their
                own terms, and we are not responsible for their availability,
                performance, security or fees.
            </p>
        </LegalSection>

        <LegalSection title="14. Disclaimer of Warranties">
            <p>
                THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS
                AVAILABLE&quot;, WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
                IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                PARTICULAR PURPOSE, TITLE AND NON-INFRINGEMENT. WE DO NOT
                WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, TIMELY, SECURE
                OR ERROR-FREE, THAT DATA SHOWN WILL BE ACCURATE, OR THAT DEFECTS
                WILL BE CORRECTED.
            </p>
        </LegalSection>

        <LegalSection title="15. Limitation of Liability">
            <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE AND OUR CONTRIBUTORS,
                SERVICE PROVIDERS AND LICENSORS WILL NOT BE LIABLE FOR ANY
                INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY OR
                PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, DATA, GOODWILL OR
                DIGITAL ASSETS, ARISING OUT OF OR RELATING TO THE SERVICE,
                HOWEVER CAUSED AND UNDER ANY THEORY OF LIABILITY. OUR TOTAL
                AGGREGATE LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICE WILL
                NOT EXCEED THE GREATER OF ONE HUNDRED US DOLLARS (USD 100) OR
                THE FEES YOU PAID US IN THE TWELVE MONTHS BEFORE THE EVENT
                GIVING RISE TO THE CLAIM. SOME JURISDICTIONS DO NOT ALLOW
                CERTAIN LIMITATIONS, SO PARTS OF THIS SECTION MAY NOT APPLY TO
                YOU.
            </p>
        </LegalSection>

        <LegalSection title="16. Indemnification">
            <p>
                You will indemnify, defend and hold harmless Snappy Trade and
                its team members, contractors and service providers from and
                against any claims, damages, losses and expenses (including
                reasonable legal fees) arising out of or related to your use of
                the Service, your breach of these Terms, or your violation of
                any law or third-party right.
            </p>
        </LegalSection>

        <LegalSection title="17. Termination">
            <p>
                You may stop using the Service at any time. Signing out, or
                choosing to forget the wallet, deletes its key from this browser
                but does not move your funds, which stay reachable with your
                recovery phrase or your Snappy backup. We may suspend or
                terminate your access to the Service at any time, with or
                without cause; because the Service is self-custody software,
                this never gives us control of your funds. Sections of these
                Terms that by their nature should survive termination (including
                Sections 8, 9, 10, 12 and 14 to 19) survive.
            </p>
        </LegalSection>

        <LegalSection title="18. Force Majeure">
            <p>
                We are not liable for any failure or delay caused by events
                beyond our reasonable control, including natural disasters, war,
                terrorism, labour disputes, government action, power or internet
                failures, blockchain network failures or attacks, and
                denial-of-service attacks.
            </p>
        </LegalSection>

        <LegalSection title="19. Governing Law and Disputes">
            <p>
                These Terms are governed by the laws of the Republic of Panama,
                without regard to conflict-of-laws principles. Any dispute
                arising out of or relating to these Terms or the Service will be
                submitted to the exclusive jurisdiction of the courts of the
                Republic of Panama. To the extent permitted by law, you agree
                that any dispute will be brought in your individual capacity
                only, and not as a plaintiff or class member in any class,
                collective or representative proceeding.
            </p>
        </LegalSection>

        <LegalSection title="20. Changes to these Terms">
            <p>
                We may update these Terms from time to time. The current
                version, with its effective date, will always be available at
                this page, and material changes may additionally be announced in
                the interface. Your continued use of the Service after an update
                constitutes acceptance of the updated Terms.
            </p>
        </LegalSection>

        <LegalSection title="21. General">
            <p>
                These Terms, together with the Privacy Policy, are the entire
                agreement between you and us regarding the Service. If any
                provision is held unenforceable, it will be modified to the
                minimum extent necessary and the remainder will stay in effect.
                Our failure to enforce a provision is not a waiver. You may not
                assign these Terms; we may assign them in connection with a
                reorganization or transfer of the Service.
            </p>
        </LegalSection>

        <LegalSection title="22. Contact">
            <p>
                Questions about these Terms: <strong>{SUPPORT_EMAIL}</strong>
            </p>
        </LegalSection>
    </LegalLayout>
);

export default Terms;
